import { gql, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { GET_USER_SOCIAL_SECURITY_NUMBER } from '../pages/user-account.page/social-security-number';
import toast from 'react-hot-toast';
import useUser from './use-user';
import { useState } from 'react';

export default function useBankID() {
  const { login } = useUser();
  const { t } = useTranslation();
  const [orderRef, setOrderRef] = useState<string>('')
  const [orderStatus, setOrderStatus] = useState<string>('')
  const [orderHint, setOrderHint] = useState<string>('')
  const [qrCode, setQrCode] = useState<string>('')
  const [autoStartToken, setAutoStartToken] = useState<string>('')
  const [socialSecurityNumber, setSocialSecurityNumber] = useState<string>('')

  /**
   * Open BankID on this device
   * @param autoStartToken
   */
  const openBankIdOnThisDevice = (autoStartToken: string): void => {
    document.location.href = `bankid:///?autostarttoken=${autoStartToken}&redirect=null`
  }

  /**
   * Reset state variables
   */
  const resetStates = (): void => {
    setOrderRef('')
    setOrderStatus('')
    setOrderHint('')
    setQrCode('')
    setAutoStartToken('')
    setSocialSecurityNumber('')
  }

  const [
    authOrderBankId,
    {
      data: authOrderBankIdData,
      loading: authOrderBankIdLoading,
      error: authOrderBankIdError,
    },
  ] = useMutation(
    gql`
      mutation AuthOrderBankId($useQr: Boolean) {
        authOrderBankId(useQr: $useQr)
      }
    `,
    {
      onCompleted({ authOrderBankId }) {
        // Update state variables
        setOrderRef(authOrderBankId?.orderRef ?? '')
        setQrCode(authOrderBankId?.qrCode ?? '')
        setAutoStartToken(authOrderBankId?.autoStartToken ?? '')
        setOrderStatus('initPolling')
      },
      onError(e) {
        resetStates()
        toast.error(t(`BankID_${e.message}`));
      },
    },
  );

  const [
    signOrderBankId,
    {
      data: signOrderBankIdData,
      loading: signOrderBankIdLoading,
      error: signOrderBankIdError,
    },
  ] = useMutation(
    gql`
      mutation SignOrderBankId($userVisibleData: String!) {
        signOrderBankId(userVisibleData: $userVisibleData)
      }
    `,
    {
      onCompleted({ signOrderBankId }) {
        // Update state variables
        setOrderRef(signOrderBankId?.orderRef ?? '')
        setQrCode(signOrderBankId?.qrCode ?? '')
        setAutoStartToken(signOrderBankId?.autoStartToken ?? '')
        setOrderStatus('initPolling')
      },
      onError(e) {
        resetStates()
        toast.error(t(`BankID_${e.message}`));
      },
    },
  );

  const [
    cancelOrderBankId,
    {
      data: cancelOrderBankIdData,
      loading: cancelOrderBankIdLoading,
      error: cancelOrderBankIdError,
    },
  ] = useMutation(
    gql`
      mutation CancelOrderBankId($orderRef: String!) {
        cancelOrderBankId(orderRef: $orderRef)
      }
    `,
    {
      onCompleted({ cancelOrderBankId }) {
        setOrderStatus(cancelOrderBankId?.status ?? 'cancel')
        setOrderHint(cancelOrderBankId?.hintCode ?? 'cancel')
        setAutoStartToken('')
        setSocialSecurityNumber('')
        setQrCode('')
        setOrderRef('')
      },
      onError(e) {
        toast.error(t(`BankID_${e.message}`));
      },
    },
  );

  const [
    collectOrderBankId,
    {
      data: collectOrderBankIdData,
      loading: collectOrderBankIdLoading,
      error: collectOrderBankIdError,
    },
  ] = useMutation(
    gql`
      mutation CollectOrderBankId($orderRef: String!) {
        collectOrderBankId(orderRef: $orderRef)
      }
    `,
    {
      onCompleted({ collectOrderBankId }) {
        // Update states
        setOrderStatus(collectOrderBankId?.status ?? '')
        setOrderHint(collectOrderBankId?.hintCode ?? '')
        setQrCode(collectOrderBankId?.qrCode ?? '')
        setSocialSecurityNumber(collectOrderBankId?.socialSecurityNumber ?? '')
      },
      onError(e) {
        resetStates()
        toast.error(t(`BankID_${e.message}`));
      },
    },
  );

  const [
    connectBankId,
    {
      data: connectBankIdData,
      loading: connectBankIdLoading,
      error: connectBankIdError,
    },
  ] = useMutation(
    gql`
      mutation ConnectBankId($socialSecurityNumber: String!, $orderRef: String!) {
        connectBankId(socialSecurityNumber: $socialSecurityNumber, orderRef: $orderRef)
      }
    `,
    {
      onCompleted({ connectBankId }) {
        if (connectBankId) {
          resetStates()
          toast.success(t(`BankID_connectSuccessful`));
        }
      },
      update(cache, { data: { connectBankId } }) {
        try {
          if (connectBankId) {
            const socialSecurityNumber = connectBankId ?? ''
            const existing = cache.readQuery({
              query: GET_USER_SOCIAL_SECURITY_NUMBER,
            });

            const user = {
              ...(existing as any),
              socialSecurityNumber,
            };

            cache.writeQuery({
              query: GET_USER_SOCIAL_SECURITY_NUMBER,
              data: {
                user,
              },
            });
          }
        } catch (e: any) {
          toast.error(e.message);
        }
      },
      onError(e) {
        setOrderStatus('failed')
        setOrderHint(e.message)
        toast.error(t(`BankID_${e.message}`));
      },
    },
  );

  const [
    signInBankId,
    {
      data: signInBankIdData,
      loading: signInBankIdLoading,
      error: signInBankIdError,
    },
  ] = useMutation(
    gql`
      mutation SignInBankId($socialSecurityNumber: String!, $orderRef: String!) {
        signInBankId(socialSecurityNumber: $socialSecurityNumber, orderRef: $orderRef)
      }
    `,
    {
      onCompleted({ signInBankId }) {
        if (signInBankId !== undefined && signInBankId !== '') {
          login(signInBankId)
          resetStates()
        }
      },
      onError(e) {
        setOrderStatus('failed')
        setOrderHint(e.message)
        toast.error(t(`BankID_${e.message}`));
      },
    },
  );

  const [
    educationCompanyUserAuthenticationBankId,
    {
      data: educationCompanyUserAuthenticationBankIdData,
      loading: educationCompanyUserAuthenticationBankIdLoading,
      error: educationCompanyUserAuthenticationBankIdError,
    },
  ] = useMutation(
    gql`
      mutation EducationCompanyUserAuthenticationBankId(
        $orderRef: String!
        $educationCompanyToken: String!
      ) {
        educationCompanyUserAuthenticationBankId(
          orderRef: $orderRef
          educationCompanyToken: $educationCompanyToken
        )
      }
    `,
    {
      onCompleted() {
        // Complete
        console.log('educationCompanyUserAuthenticationBankId --> onCompleted');
      },
      onError(e) {
        toast.error(t(`BankID_${e.message}`));
      },
    },
  );

  const [
    educationCompanySaveBankId,
    {
      data: educationCompanySaveBankIdData,
      loading: educationCompanySaveBankIdLoading,
      error: educationCompanySaveBankIdError,
    },
  ] = useMutation(
    gql`
      mutation EducationCompanySaveBankId(
        $orderRef: String!
        $courseInstance: Object!
      ) {
        educationCompanySaveBankId(
          orderRef: $orderRef
          courseInstance: $courseInstance
        )
      }
    `,
    {
      onCompleted() {
        console.log('EducationCompanySaveBankId --> onCompleted');
        toast.success(t('Saved'));
      },
      onError(e) {
        toast.error(t(`BankID_${e.message}`));
      },
    },
  );

  return {
    // methods
    openBankIdOnThisDevice,

    // state
    orderRef,
    orderStatus,
    orderHint,
    autoStartToken,
    qrCode,
    socialSecurityNumber,

    // setters
    setOrderHint,

    // auth
    authOrderBankId,
    authOrderBankIdData,
    authOrderBankIdLoading,
    authOrderBankIdError,
    // sign
    signOrderBankId,
    signOrderBankIdData,
    signOrderBankIdLoading,
    signOrderBankIdError,
    // cancel
    cancelOrderBankId,
    cancelOrderBankIdData,
    cancelOrderBankIdLoading,
    cancelOrderBankIdError,
    // collect
    collectOrderBankId,
    collectOrderBankIdData,
    collectOrderBankIdLoading,
    collectOrderBankIdError,

    // specific
    connectBankId,
    connectBankIdData,
    connectBankIdLoading,
    connectBankIdError,
    signInBankId,
    signInBankIdData,
    signInBankIdLoading,
    signInBankIdError,
    educationCompanyUserAuthenticationBankId,
    educationCompanyUserAuthenticationBankIdData,
    educationCompanyUserAuthenticationBankIdLoading,
    educationCompanyUserAuthenticationBankIdError,
    educationCompanySaveBankId,
    educationCompanySaveBankIdData,
    educationCompanySaveBankIdLoading,
    educationCompanySaveBankIdError,
  };
}
